import { useEffect, useState } from 'react';
import useUserContext from './useUserContext';

const useVideoVoting = (watchPartyId: string, queue: { title: string; url: string }[]) => {
  const { user, socket } = useUserContext();
  const [votes, setVotes] = useState<Record<string, Set<string>>>({}); // Track user votes per video
  const [activeVote, setActiveVote] = useState<{ videoTitle: string; votes: number }[] | null>(
    null,
  );
  // reorder the videos based on votes as indices for the queue
  const [activeReorderVote, setActiveReorderVote] = useState<
    { videoTitle: string; votes: number }[] | null
  >(null);

  const [userHasVoted, setUserHasVoted] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Listen for voteStarted event
    socket.on('serverVoteStarted', () => {
      const videoTitles = queue.map(video => video.title);
      setActiveVote(
        videoTitles.map(title => ({
          videoTitle: title,
          votes: votes[title] ? votes[title].size : 0,
        })),
      );
      setVotes({});
      setUserHasVoted({});
      setError(null);
    });

    // Listen for voteUpdated event
    socket.on('serverVoteUpdated', (data: { videoTitle: string; username: string }) => {
      setVotes(prevVotes => ({
        ...prevVotes,
        [data.videoTitle]: new Set([...(prevVotes[data.videoTitle] || []), data.username]),
      }));
      setUserHasVoted(prev => ({ ...prev, [data.videoTitle]: true }));
      setActiveVote(prev => {
        if (prev) {
          return prev.map(video => {
            if (video.videoTitle === data.videoTitle) {
              return { ...video, votes: video.votes + 1 };
            }
            return video;
          });
        }
        return prev;
      });
      setError(null);
    });

    // Listen for serverConcludeVote event
    socket.on('serverConcludeVote', (data: { videoTitle: string }) => {
      // Remove the video from the queue
      socket.emit('removeVideoFromQueue', {
        watchPartyId,
        data: {
          title: data.videoTitle,
          url: queue.find(video => video.title === data.videoTitle)?.url as string,
        },
      });
      // Reset the votes and active vote
      setActiveVote(null);
      setVotes({});
      setUserHasVoted({});
      setError(null);
    });

    socket.on('serverReorderVoteStarted', () => {
      const videoTitles = queue.map(video => video.title);
      setActiveReorderVote(
        videoTitles.map(title => ({
          videoTitle: title,
          votes: votes[title] ? votes[title].size : 0,
        })),
      );
      setVotes({});
      setUserHasVoted({});
      setError(null);
    });

    socket.on('serverReorderVoteUpdated', (data: { videoTitle: string; username: string }) => {
      // users can vote for the same video multiple times
      setVotes(prevVotes => ({
        ...prevVotes,
        [data.videoTitle]: new Set([...(prevVotes[data.videoTitle] || []), data.username]),
      }));
      setUserHasVoted(prev => ({ ...prev, [data.videoTitle]: true }));
      setActiveReorderVote(prev => {
        if (prev) {
          return prev.map(video => {
            if (video.videoTitle === data.videoTitle) {
              return { ...video, votes: video.votes + 1 };
            }
            return video;
          });
        }
        return prev;
      });
      setError(null);
    });

    socket.on('serverConcludeReorderVote', (recievedQueue: { title: string; url: string }[]) => {
      if (activeReorderVote) {
        // the video with the mose votes will be the first in the queue, then the second, etc.
        const reorderedQueue = recievedQueue.sort((a, b) => {
          const aVotes = activeReorderVote.find(video => video.videoTitle === a.title)?.votes || 0;
          const bVotes = activeReorderVote.find(video => video.videoTitle === b.title)?.votes || 0;
          return bVotes - aVotes;
        });
        socket.emit('reorderQueue', watchPartyId, reorderedQueue);
      }
      setActiveReorderVote(null);
      setVotes({});
      setUserHasVoted({});
      setError(null);
    });

    return () => {
      socket.off('serverVoteStarted');
      socket.off('serverConcludeVote');
      socket.off('serverVoteUpdated');
      socket.off('serverReorderVoteStarted');
      socket.off('serverReorderVoteUpdated');
      socket.off('serverConcludeReorderVote');
    };
  }, [activeReorderVote, activeVote, queue, socket, votes, watchPartyId]);

  // Start a new vote (can only be done after cooldown)
  const startVote = () => {
    const videoTitles = queue.map(video => video.title);
    setActiveVote(
      videoTitles.map(title => ({
        videoTitle: title,
        votes: votes[title] ? votes[title].size : 0,
      })),
    );
    setVotes({});
    setUserHasVoted({});
    setError(null);
    socket.emit('voteStarted', watchPartyId);
  };

  // Vote for an active video
  const voteForVideoRemove = (videoTitle: string) => {
    if (activeVote && !userHasVoted[videoTitle]) {
      setVotes(prevVotes => ({
        ...prevVotes,
        [videoTitle]: new Set([...(prevVotes[videoTitle] || []), user.username]),
      }));
      setUserHasVoted(prev => ({ ...prev, [videoTitle]: true }));
      setActiveVote(prev => {
        if (prev) {
          return prev.map(video => {
            if (video.videoTitle === videoTitle) {
              return { ...video, votes: video.votes + 1 };
            }
            return video;
          });
        }
        return prev;
      });
      socket.emit('voteUpdated', watchPartyId, { videoTitle, username: user.username });
    } else {
      setError('You have already voted for this video.');
    }
  };

  // Conclude the vote
  const concludeVote = () => {
    if (activeVote) {
      const winningVideo = activeVote.reduce((prev, current) =>
        prev.votes > current.votes ? prev : current,
      );
      socket.emit('concludeVote', watchPartyId, winningVideo.videoTitle);
      socket.emit('removeVideoFromQueue', {
        watchPartyId,
        data: {
          title: winningVideo.videoTitle,
          url: queue.find(video => video.title === winningVideo.videoTitle)?.url as string,
        },
      });

      setActiveVote(null);
      setVotes({});
      setUserHasVoted({});
    }
  };

  const concludeWithNoWinner = () => {
    setActiveVote(null);
    setVotes({});
    setUserHasVoted({});
    setError(null);
    socket.emit('concludeVote', watchPartyId, '');
  };

  const startReorderQueue = () => {
    const videoTitles = queue.map(video => video.title);
    setActiveReorderVote(
      videoTitles.map(title => ({
        videoTitle: title,
        votes: votes[title] ? votes[title].size : 0,
      })),
    );
    setVotes({});
    setUserHasVoted({});
    setError(null);
    socket.emit('reorderVoteStarted', watchPartyId);
  };

  const voteForReorder = (videoTitle: string) => {
    // users can vote for the same video multiple times
    if (activeReorderVote && !userHasVoted[videoTitle]) {
      setVotes(prevVotes => ({
        ...prevVotes,
        [videoTitle]: new Set([...(prevVotes[videoTitle] || []), user.username]),
      }));
      setUserHasVoted(prev => ({ ...prev, [videoTitle]: true }));
      setActiveReorderVote(prev => {
        if (prev) {
          return prev.map(video => {
            if (video.videoTitle === videoTitle) {
              return { ...video, votes: video.votes + 1 };
            }
            return video;
          });
        }
        return prev;
      });
      socket.emit('reorderVoteUpdated', watchPartyId, { videoTitle, username: user.username });
    } else {
      setError('You have already voted for this video.');
    }
  };

  const concludeReorderQueue = () => {
    if (activeReorderVote) {
      // Reorder the queue based on the votes as indices
      const reorderedQueue = queue.sort((a, b) => {
        const aVotes = activeReorderVote.find(video => video.videoTitle === a.title)?.votes || 0;
        const bVotes = activeReorderVote.find(video => video.videoTitle === b.title)?.votes || 0;
        return bVotes - aVotes;
      });

      socket.emit('concludeReorderVote', watchPartyId, reorderedQueue);
      socket.emit('reorderQueue', watchPartyId, reorderedQueue);

      setActiveReorderVote(null);
      setVotes({});
      setUserHasVoted({});
      setError(null);
    }
  };

  return {
    votes,
    userHasVoted,
    activeVote,
    error,
    activeReorderVote,
    startVote,
    voteForVideoRemove,
    concludeVote,
    concludeWithNoWinner,
    startReorderQueue,
    voteForReorder,
    concludeReorderQueue,
  };
};

export default useVideoVoting;
